import type { AssetAdministrationShell } from '../Metamodel/AssetAdministrationShell';
import type { ConceptDescription } from '../Metamodel/ConceptDescription';
import type { Submodel } from '../Metamodel/Submodel';
import type { SubmodelElement } from '../Metamodel/SubmodelElements/SubmodelElement';
import type { LangString } from '../Metamodel/SupportTypes/LangString';
import type { Reference } from '../Metamodel/SupportTypes/Reference';
import { KeyTypes } from '../Metamodel/Enums/KeyTypes';
import { ModelType } from '../Metamodel/Enums/ModelType';
import { ReferenceTypes } from '../Metamodel/Enums/ReferenceTypes';

/**
 * Represents an IdShort pattern somewhere in a
 * string and contains the group "idShort".
 */
export const ID_SHORT_REGEX = /(?<idShort>[a-z]\w*)/i;

/**
 * Pattern to test whether a string is a valid IdShort.
 * It applies to the full string.
 */
export const JUST_ID_SHORT_REGEX = /^[a-z]\w*$/i;

/**
 * Represents an index pattern somewhere in a string and contains the
 * group "index".
 */
export const INDEX_REGEX = /\[(?<index>\d+)\]/;

/**
 * Represents an index pattern at the end of a string and contains the
 * group "index".
 */
export const LAST_INDEX_REGEX = /\[(?<index>\d+)\]$/;

export const langStringsToMap = (langString: LangString[]): Map<string, string> => {
  const map = new Map<string, string>();
  langString.forEach((lang) => {
    map.set(lang.language, lang.text);
  });
  return map;
};

export const mapToLangStrings = (map: Map<string, string>): LangString[] => {
  const langStrings: LangString[] = [];
  map.forEach((text, language) => {
    langStrings.push({ language, text });
  });
  return langStrings;
};

export const modelTypeToKeyType = (modelType: ModelType): KeyTypes => {
  let keyType: KeyTypes;
  switch (modelType) {
    case ModelType.AssetAdministrationShell:
      keyType = KeyTypes.AssetAdministrationShell;
      break;
    case ModelType.Submodel:
      keyType = KeyTypes.Submodel;
      break;
    case ModelType.ConceptDescription:
      keyType = KeyTypes.ConceptDescription;
      break;
    case ModelType.AnnotatedRelationshipElement:
      keyType = KeyTypes.AnnotatedRelationshipElement;
      break;
    case ModelType.Capability:
      keyType = KeyTypes.Capability;
      break;
    case ModelType.BasicEventElement:
      keyType = KeyTypes.BasicEventElement;
      break;
    case ModelType.Blob:
      keyType = KeyTypes.Blob;
      break;
    case ModelType.File:
      keyType = KeyTypes.File;
      break;
    case ModelType.MultiLanguageProperty:
      keyType = KeyTypes.MultiLanguageProperty;
      break;
    case ModelType.Property:
      keyType = KeyTypes.Property;
      break;
    case ModelType.Range:
      keyType = KeyTypes.Range;
      break;
    case ModelType.ReferenceElement:
      keyType = KeyTypes.ReferenceElement;
      break;
    case ModelType.RelationshipElement:
      keyType = KeyTypes.RelationshipElement;
      break;
    case ModelType.SubmodelElementCollection:
      keyType = KeyTypes.SubmodelElementCollection;
      break;
    case ModelType.SubmodelElementList:
      keyType = KeyTypes.SubmodelElementList;
      break;
    case ModelType.Operation:
      keyType = KeyTypes.Operation;
      break;
    case ModelType.Entity:
      keyType = KeyTypes.Entity;
      break;
    default:
      throw new Error(`Model type '${modelType}' can't be converted to a key type.`);
  }
  return keyType;
};

export const referenceFrom = (data: AssetAdministrationShell | Submodel | ConceptDescription | SubmodelElement): Reference => {
  const ref: Reference = {
    type: ReferenceTypes.ModelReference,
    keys: [],
    referredSemanticId: undefined,
  };
  switch (data.modelType) {
    case ModelType.AssetAdministrationShell:
      ref.keys.push({
        type: KeyTypes.AssetAdministrationShell,
        value: (data as AssetAdministrationShell).id,
      });
      break;
    case ModelType.Submodel:
      ref.keys.push({
        type: KeyTypes.Submodel,
        value: (data as Submodel).id,
      });
      ref.referredSemanticId = (data as Submodel).semanticId;
      break;
    case ModelType.ConceptDescription:
      ref.keys.push({
        type: KeyTypes.ConceptDescription,
        value: (data as ConceptDescription).id,
      });
      break;
    default:
    {
      const se = data as SubmodelElement;
      if (!se.idShort) {
        throw new Error('SubmodelElement does not have an idShort.');
      }
      ref.keys.push({
        type: modelTypeToKeyType(data.modelType),
        value: se.idShort,
      });
      ref.referredSemanticId = se.semanticId;
    }
  }
  return ref;
};
